import Link from 'next/link';
import { BookOpen } from 'lucide-react';
import PostAuthorAvatar from './post-author-avatar';
import PostEditLink from './post-edit-link';
import PostLikeCount from './post-like-count';
import ShareAction from './share-action';
import type { HomepageGuideAlbum } from '../lib/homepage-config';
import {
  buildArticleOutline,
  formatGuidePublishedAt,
  type GuidePostData,
} from '../lib/guide-article';
import { staticUrl } from '../lib/config/static-assets';
import { handleImgToWebp } from '../lib/config/image-webp';
import PostDetailEngagement from './post-detail-engagement';
import GuideBackRail from './guide-back-rail';
import GuideStickyTitle from './guide-sticky-title';
import GuideDetailToc from './guide-detail-toc';
import PostRichContent from './post-rich-content';
import RouteScrollReset from './route-scroll-reset';
import './game-guide-page.css';
import './game-guide-page-v2.css';
import './article-body-shared.css';

export default function GuideArticleView({
  gameKey,
  gameDomain,
  postId,
  article,
  guideAlbums,
  relatedPostTitles,
  loading = false,
  error = null,
  variant = 'default',
}: {
  gameKey: string;
  gameDomain: string;
  postId: string;
  article: GuidePostData | null;
  guideAlbums: HomepageGuideAlbum[];
  relatedPostTitles: Map<string, string> | Record<string, string>;
  loading?: boolean;
  error?: string | null;
  variant?: 'default' | 'v2';
}) {
  const titles =
    relatedPostTitles instanceof Map
      ? relatedPostTitles
      : new Map(Object.entries(relatedPostTitles));
  const outline = article
    ? buildArticleOutline(article.content)
    : { html: '', toc: [] as ReturnType<typeof buildArticleOutline>['toc'] };
  const publishedDate = article
    ? formatGuidePublishedAt(article.publishedAt)
    : '';
  const avatarUrl =
    article?.authorAvatar &&
    /^https:\/\/cdnimg(?:-[a-z0-9]+)?\.gamekee\.com\//i.test(article.authorAvatar)
      ? `/api/gamekee-avatar?url=${encodeURIComponent(article.authorAvatar)}`
      : staticUrl(
          article?.authorAvatar || '/assets/game-wiki/authors/guide-author.png',
        );
  const title = article?.title?.trim() || '';

  return (
    <main className={`data-guide-page${variant === 'v2' ? ' data-guide-page--v2' : ''}`} aria-busy={loading}>
      <RouteScrollReset />
      <div className="guide-detail-layout">
        <GuideBackRail href={`/${gameDomain}/dguides`} homeHref={`/${gameDomain}`} variant={variant} />
        <article className="guide-detail-card">
          <div className="guide-detail-heading">
            <h1 id="guide-article-title">
              {error ? '加载失败' : loading ? (
                <span className="guide-skeleton guide-skeleton-line" aria-hidden="true" />
              ) : (
                title
              )}
            </h1>
            <div className="guide-detail-meta-row">
              <div className="guide-author-line">
                {loading ? (
                  <span className="guide-skeleton guide-skeleton-avatar" aria-hidden="true" />
                ) : (
                  <PostAuthorAvatar src={avatarUrl} fallback={staticUrl("/assets/game-wiki/authors/guide-author.png")} />
                )}
                {loading ? (
                  <>
                    <span className="guide-skeleton guide-skeleton-line is-short" aria-hidden="true" />
                    <span className="guide-skeleton guide-skeleton-line is-meta" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    <strong>{article?.author || ''}</strong>
                    {article ? <span className="guide-level">Lv.{article.authorLevel ?? 1}</span> : null}
                    <span className="guide-publish-meta">
                      {publishedDate
                        ? `${publishedDate}${article?.category ? ` · ${article.category}` : ''}`
                        : ''}
                    </span>
                  </>
                )}
              </div>
              <div className="guide-detail-actions" aria-label="文章互动数据">
                {article && !loading && !error && <>
                  <PostLikeCount postId={postId} initialCount={article.likeCount} />
                  <ShareAction linkUrl={`/${gameDomain}/guide/${postId}`} title={article.title} subject="帖子" />
                  <PostEditLink gameKey={gameKey} gameDomain={gameDomain} postId={postId} />
                </>}
              </div>
            </div>
          </div>
          <GuideStickyTitle
            anchorId="guide-article-title"
            title={title}
            backHref={`/${gameDomain}/dguides`}
          />
          {error ? (
            <p className="guide-detail-inline-status" role="alert">
              {error}
            </p>
          ) : loading ? (
            <div className="guide-detail-body-skeleton" aria-hidden="true">
              <span className="guide-skeleton guide-skeleton-line" />
              <span className="guide-skeleton guide-skeleton-line" />
              <span className="guide-skeleton guide-skeleton-line is-short" />
              <span className="guide-skeleton guide-skeleton-block" />
              <span className="guide-skeleton guide-skeleton-line" />
              <span className="guide-skeleton guide-skeleton-line is-short" />
            </div>
          ) : (
            <PostRichContent html={outline.html} />
          )}
          {!loading && !error && article?.excerpt ? (
            <div className="guide-detail-note">
              <strong>资料摘录</strong>
              <span>·</span>
              <span className="guide-detail-note-text">{article.excerpt}</span>
            </div>
          ) : loading ? (
            <div className="guide-detail-note" aria-hidden="true">
              <span className="guide-skeleton guide-skeleton-line" />
            </div>
          ) : null}
        </article>
        <aside className="guide-detail-sidebar" aria-label="攻略辅助信息">
          {!loading && !error && article && outline.toc.length > 0 && <GuideDetailToc items={outline.toc} variant="desktop" />}
          {loading && (
            <section className="guide-detail-side-card" aria-hidden="true">
              <div className="guide-detail-side-heading">
                <h2>目录</h2>
              </div>
              <div className="guide-detail-body-skeleton">
                <span className="guide-skeleton guide-skeleton-line" />
                <span className="guide-skeleton guide-skeleton-line is-short" />
                <span className="guide-skeleton guide-skeleton-line" />
              </div>
            </section>
          )}
          {guideAlbums.map((album) => (
            <section
              className="guide-detail-side-card guide-detail-album"
              key={album.id}
            >
              <div className="guide-detail-side-heading">
                <h2>帖子专辑</h2>
              </div>
              <div className="guide-detail-album-name">
                {(album.cover || variant === 'v2') && (
                  <span
                    className={album.cover ? 'has-cover' : 'is-placeholder'}
                    style={{
                      backgroundImage: album.cover ? `url("${handleImgToWebp(album.cover).replaceAll('"', '%22')}")` : undefined,
                    }}
                  >
                    {variant === 'v2' && <BookOpen aria-hidden="true" size={15} strokeWidth={1.8} />}
                  </span>
                )}
                <strong>{album.title}</strong>
              </div>
              <div className="guide-detail-album-posts">
                {album.postIds.map((id, index) => (
                  <Link
                    className={id === postId ? 'is-current' : undefined}
                    href={`/${gameDomain}/guide/${id}`}
                    key={id}
                  >
                    <small>{String(index + 1).padStart(2, '0')}</small>
                    <span>{titles.get(id) || ''}</span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </aside>
        <div className="guide-detail-comments">
          {article && !loading && !error ? (
            <PostDetailEngagement
              gameKey={gameKey}
              gameDomain={gameDomain}
              postId={postId}
              title={article.title}
              tocItems={outline.toc}
            />
          ) : (
            <section className="guide-detail-side-card" aria-hidden={loading || undefined}>
              <div className="guide-detail-side-heading">
                <h2>评论</h2>
              </div>
              {error ? (
                <p className="guide-detail-inline-status" role="alert">
                  评论暂不可用
                </p>
              ) : (
                <div className="guide-detail-body-skeleton" aria-hidden="true">
                  <span className="guide-skeleton guide-skeleton-line" />
                  <span className="guide-skeleton guide-skeleton-line is-short" />
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </main>
  );
}
